<template>
    <section class="slice slice-trends-top-skills" :class="{'slice-trends-top-skills--grey': backgroundColor === 'grey'}">
        <div class="slice-content">
            <div class="slice-trends-top-skills__panel">
                <PrismicRichText class="slice-trends-top-skills__title" :field="slice.primary.title" />
                <div class="slice-trends-top-skills__columns">
                    <div v-for="(column, index) in columns" :key="`trends-top-skills-${index}`" class="slice-trends-top-skills__column">
                        <h3 class="slice-trends-top-skills__subtitle">{{ column.title }}</h3>
                        <TrendsTagList :tags="column.tags" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled, type Content, type LinkField} from '@prismicio/client';

    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {TrendTagItem} from '~/components/trends/TrendsTag.vue';
    import TrendsTagList from '~/components/trends/TrendsTagList.vue';

    const {slice} = defineProps(getSliceComponentProps<Content.TrendsTopSkillsSlice>(['slice', 'index', 'slices', 'context']));

    type TrendTag = {text: string; href: string};

    function linkFieldToTrendTag(link: LinkField): TrendTagItem | null {
        if (!link.text) return null;

        const isLinkFilled = isFilled.link(link);
        return {text: link.text, href: isLinkFilled ? link.url : undefined};
    }

    function mapRepeatableLinksToTrendTags(links: readonly LinkField[]): TrendTag[] {
        return links.map(linkFieldToTrendTag).filter((t): t is TrendTag => t !== null);
    }

    type Column = {title: string; tags: TrendTag[]};

    const columns = computed<Column[]>(() =>
        slice.primary.columns.map((column) => ({
            title: column.title ?? '',
            tags: mapRepeatableLinksToTrendTags(column.tags),
        })),
    );
    const backgroundColor = computed(() => slice.primary.backgroundColor);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-trends-top-skills {
        --background-color-top: var(--joy-color-neutral-0);
        --background-color-bottom: var(--joy-color-neutral-20);
        background-color: var(--background-color-bottom);

        &--grey {
            --background-color-top: var(--joy-color-neutral-20);
            --background-color-bottom: var(--joy-color-neutral-0);
        }

        &:before {
            content: '';
            display: block;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 200px;
            background-color: var(--background-color-top);
        }
        &__panel {
            position: relative;
            text-align: left;
            background-color: var(--background-color-bottom);
            padding: var(--joy-core-spacing-9);
            border-radius: var(--joy-core-radius-4);

            @include mq.screen_md(min) {
                padding: var(--joy-core-spacing-9);
            }
        }
        &__title {
            margin-bottom: var(--joy-core-spacing-8);
            h2 {
                font-size: var(--joy-font-size-secondary-400);
                font-weight: var(--joy-font-weight-normal);
            }
        }
        &__columns {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-6);

            @include mq.screen_md(min) {
                flex-direction: row;

                > * {
                    flex: 1 1 0%;
                }
            }
        }

        &__subtitle {
            font-size: var(--joy-font-size-primary-800);
            margin-bottom: var(--joy-core-spacing-4);
        }

        &__tags {
            display: flex;
            flex-wrap: wrap;
            gap: var(--joy-core-spacing-3);
        }
    }
</style>
